/**
 * Phase 46 — Feature #4. Admin Notes module.
 * Independent sidebar page: create, edit, delete, search, pin. Timestamped.
 */
import React, { useEffect, useMemo, useState } from "react";
import { PageHeader } from "./UsmLayout";
import HubTabs from "./hub/HubTabs";
import usmApi from "@/lib/usmApi";
import { toast } from "sonner";
import { Pin, PinOff, Trash2, Plus, Search } from "lucide-react";

export default function NotesPage() {
  const [notes, setNotes] = useState([]);
  const [q, setQ] = useState("");
  const [draft, setDraft] = useState({ title: "", body: "" });
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const r = await usmApi.get("/notes", { params: q ? { q } : {} });
      setNotes(r.data || []);
    } catch (e) { toast.error("Notlar yüklenemedi"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [q]);          // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-save draft to localStorage so an accidental refresh doesn't lose it.
  useEffect(() => {
    const saved = localStorage.getItem("usm.notes.draft");
    if (saved) try { setDraft(JSON.parse(saved)); } catch {}
  }, []);
  useEffect(() => {
    localStorage.setItem("usm.notes.draft", JSON.stringify(draft));
  }, [draft]);

  const save = async () => {
    if (!draft.body?.trim()) { toast.error("Not içeriği boş olamaz"); return; }
    try {
      if (editingId) {
        const r = await usmApi.patch(`/notes/${editingId}`, draft);
        setNotes((prev) => prev.map((n) => n.id === editingId ? r.data : n));
        toast.success("Not güncellendi");
      } else {
        const r = await usmApi.post("/notes", { ...draft, pinned: false });
        setNotes((prev) => [r.data, ...prev]);
        toast.success("Not eklendi");
      }
      setDraft({ title: "", body: "" });
      setEditingId(null);
      localStorage.removeItem("usm.notes.draft");
    } catch (e) { toast.error(e?.response?.data?.detail || "Kaydedilemedi"); }
  };

  const togglePin = async (n) => {
    try {
      const r = await usmApi.patch(`/notes/${n.id}`, { pinned: !n.pinned });
      setNotes((prev) => [...prev.map((x) => x.id === n.id ? r.data : x)].sort(sortNotes));
    } catch { toast.error("Sabitlenemedi"); }
  };

  const remove = async (n) => {
    if (!confirm("Bu notu silmek istiyor musunuz?")) return;
    try {
      await usmApi.delete(`/notes/${n.id}`);
      setNotes((prev) => prev.filter((x) => x.id !== n.id));
    } catch { toast.error("Silinemedi"); }
  };

  const startEdit = (n) => {
    setDraft({ title: n.title || "", body: n.body || "" });
    setEditingId(n.id);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const list = useMemo(() => [...notes].sort(sortNotes), [notes]);

  return (
    <div>
      <HubTabs section="branches" />
      <PageHeader title="Notlar" breadcrumbs={[{ label: "USM", to: "/usm" }, { label: "Notlar" }]} />

      <div className="bg-white border border-[#E7E5E4] p-5 mb-6">
        <input
          data-testid="notes-title-input"
          value={draft.title}
          onChange={(e) => setDraft((d) => ({ ...d, title: e.target.value }))}
          placeholder="Başlık (opsiyonel)"
          className="w-full px-3 py-2 border border-[#E7E5E4] focus:border-[#C8A96A] outline-none text-sm mb-2"
        />
        <textarea
          data-testid="notes-body-input"
          value={draft.body}
          onChange={(e) => setDraft((d) => ({ ...d, body: e.target.value }))}
          placeholder="Not içeriği…"
          rows={4}
          className="w-full px-3 py-2 border border-[#E7E5E4] focus:border-[#C8A96A] outline-none text-sm resize-none"
        />
        <div className="flex justify-end gap-2 mt-3">
          {editingId && (
            <button onClick={() => { setEditingId(null); setDraft({ title: "", body: "" }); }} className="text-[11px] uppercase tracking-[0.2em] px-4 py-2 border border-[#E7E5E4] hover:bg-[#FAFAF9]">İptal</button>
          )}
          <button data-testid="notes-save-btn" onClick={save} className="bg-[#0A0A0A] text-white text-[11px] uppercase tracking-[0.2em] px-4 py-2 inline-flex items-center gap-2">
            <Plus size={14} /> {editingId ? "Güncelle" : "Kaydet"}
          </button>
        </div>
      </div>

      <div className="mb-4 relative max-w-md">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#71717A]" />
        <input
          data-testid="notes-search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Notlarda ara…"
          className="w-full pl-9 pr-3 py-2 border border-[#E7E5E4] focus:border-[#C8A96A] outline-none text-sm bg-white"
        />
      </div>

      {loading ? (
        <p className="text-sm text-[#71717A]">Yükleniyor…</p>
      ) : list.length === 0 ? (
        <p className="text-sm text-[#71717A]">Henüz not yok.</p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {list.map((n) => (
            <div key={n.id} data-testid={`note-card-${n.id}`} className="bg-white border border-[#E7E5E4] p-4 relative">
              <div className="flex items-start justify-between gap-2 mb-2">
                <div className="min-w-0">
                  {n.title && <p className="font-semibold text-sm truncate">{n.title}</p>}
                  <p className="text-[10px] text-[#71717A] uppercase tracking-wider">
                    {n.author_name || "—"} · {new Date(n.updated_at).toLocaleString("tr-TR")}
                  </p>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <button onClick={() => togglePin(n)} title={n.pinned ? "Sabitlemeyi kaldır" : "Sabitle"} className="p-1 hover:bg-[#FAFAF9] text-[#71717A]">
                    {n.pinned ? <Pin size={13} className="text-[#C8A96A]" /> : <PinOff size={13} />}
                  </button>
                  <button onClick={() => remove(n)} className="p-1 hover:bg-[#FAFAF9] text-[#71717A]">
                    <Trash2 size={13} />
                  </button>
                </div>
              </div>
              <p className="text-sm text-[#0A0A0A] whitespace-pre-wrap break-words cursor-pointer" onClick={() => startEdit(n)}>{n.body}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function sortNotes(a, b) {
  if (!!b.pinned !== !!a.pinned) return b.pinned ? 1 : -1;
  return (b.updated_at || "").localeCompare(a.updated_at || "");
}
